import { PRIORITY_META, STATUS_META, TASK_TYPE_META, type Priority, type TaskDTO, type TaskStatus } from '@taskos/shared';
import { AlarmClock, Bell, Lock, Paperclip, Pin, Repeat, User } from 'lucide-react';
import { formatDateTime, formatDue } from '../../lib/format';

export function PriorityBadge({ p, short }: { p: Priority; short?: boolean }) {
  return (
    <span className={`prio ${p}`} title={`Priority: ${PRIORITY_META[p].label}`}>
      {short ? <span className="sr-only">{PRIORITY_META[p].label}</span> : PRIORITY_META[p].label}
    </span>
  );
}

const STATUS_CLASS: Record<TaskStatus, string> = {
  INBOX: 'info',
  PLANNED: '',
  IN_PROGRESS: 'primary',
  WAITING: 'warning',
  COMPLETED: 'success',
  CANCELLED: '',
  ARCHIVED: '',
};
export function StatusBadge({ s }: { s: TaskStatus }) {
  return <span className={`badge ${STATUS_CLASS[s]}`}>{STATUS_META[s].label}</span>;
}

export function DueLabel({ t }: { t: TaskDTO }) {
  if (!t.dueDate) return <span className="muted">No date</span>;
  return (
    <span className={t.isOverdue ? 'overdue-text' : undefined} title={t.dueRule?.mode === 'RELATIVE' ? `Set as +${t.dueRule.amount}${t.dueRule.workingDays ? ' working' : ''} days` : undefined}>
      {t.isOverdue && '⚠ '}
      {formatDue(t)}
    </span>
  );
}

/** Compact metadata line shared by tiles and rows. */
export function TaskMeta({ t, compact }: { t: TaskDTO; compact?: boolean }) {
  return (
    <div className="meta">
      <span>
        <DueLabel t={t} />
      </span>
      {!compact && t.status !== 'PLANNED' && (
        <span>
          <StatusBadge s={t.status} />
        </span>
      )}
      {t.assignee && (
        <span title="Assigned to">
          <User size={13} aria-hidden />
          {t.assignee.name}
        </span>
      )}
      {(t.project || t.category) && <span className="truncate">{[t.project?.name, t.category?.name].filter(Boolean).join(' · ')}</span>}
      {t.nextReminderAt && (
        <span title={`Next reminder ${formatDateTime(t.nextReminderAt)}`}>
          <Bell size={13} aria-hidden />
          <span className="sr-only">Reminder set</span>
        </span>
      )}
      {t.snoozedUntil && (
        <span title={`Snoozed until ${formatDateTime(t.snoozedUntil)}`}>
          <AlarmClock size={13} aria-hidden /> {compact ? '' : 'Snoozed'}
          <span className="sr-only">Snoozed</span>
        </span>
      )}
      {(t.followUpOf || t.openFollowUps > 0 || t.taskType === 'FOLLOW_UP') && (
        <span title={t.followUpOf ? `Follow-up of ${t.followUpOf.ref}` : `${t.openFollowUps} open follow-up(s)`}>
          <Pin size={13} aria-hidden />
          <span className="sr-only">Follow-up</span>
        </span>
      )}
      {t.recurrence && (
        <span title={t.recurrence.summary}>
          <Repeat size={13} aria-hidden />
          <span className="sr-only">Recurring</span>
        </span>
      )}
      {t.attachmentCount > 0 && (
        <span title={`${t.attachmentCount} attachment(s)`}>
          <Paperclip size={13} aria-hidden />
          {t.attachmentCount}
        </span>
      )}
      {t.visibility === 'PRIVATE' && (
        <span title="Private task">
          <Lock size={13} aria-hidden />
          <span className="sr-only">Private</span>
        </span>
      )}
    </div>
  );
}

export function typeLabel(t: TaskDTO) {
  const m = TASK_TYPE_META[t.taskType];
  return `${m.emoji} ${m.label}`;
}
